import CircularProgress from "@mui/material/CircularProgress";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AlertWrapper from "~/src/components/Alerts/AlertWrapper";
import { BottomBarDouble } from "~/src/components/BottomBars/BottomBars";
import BottomLine from "~/src/components/BottomLine";
import ErrorList from "~/src/components/Form/ErrorList";
import { DeleteIcon, EditIcon } from "~/src/components/Icons/Icons";
import Text, { Header, TextVariant } from "~/src/components/Text/Text";
import { StorageContext, StorageItem } from "~/src/contexts/StorageContext/StorageProvider";
import useJaktdataHelper from "~/src/contexts/StorageContext/useJaktdataHelper";
import { sumSettOgSkuttJaktdag, sumSettOgSkuttJaktdagDefault } from "~/src/helpers/jaktdagHelper";
import { useClientState } from "~/src/hooks/useClientState";
import { ApiError, Individ, Jaktdag, JaktdagResponse, Område } from "../../api/types";
import { jaktdataApi } from "../../appGlobals";
import { AccordionDefault } from "../../components/Accordion/AccordionDefault";
import AlertDefault, { AlertVariant } from "../../components/Alerts/AlertDefault";
import ButtonDefault, { ButtonVariant } from "../../components/Button/ButtonDefault";
import SkuttDyrCard from "../../components/SkuttDyrCard/SkuttDyrCard";
import AppDataContext from "../../contexts/AppDataContext/DataProvider";
import UIContext from "../../contexts/UIContext/UIProvider";
import { getOmradeForJaktdag } from "../../helpers/omradeHelper";
import { formatDmy, hentJaktaar } from "../../helpers/time";
import { ArtType, SumSettOgSkuttJaktdag } from "../../helpers/types";
import { useConfirmationDialog } from "../../hooks/useConfirmationDialog";
import { useStateful } from "../../hooks/useStateful";
import { getJaktdagQuery } from "../../react-query/queries";
import { Terreng } from "../Registrering/types";
import { CounterWithLabel } from "./Components/Counter";
import TomtResultat from "./Components/TomtResultat";

export default function JaktdagDetaljer({ setHeader }: { setHeader: (e: string) => void }) {
  const tBase = "pages.oversikt.jaktdagDetaljer.";
  const navigate = useNavigate();
  const appData = useContext(AppDataContext);
  const queryClient = useQueryClient();
  const { jaktdagId } = useParams();
  const omrade = useStateful<Område | null | undefined>(null);
  const canEdit = useStateful(false);
  const uiContext = useContext(UIContext);
  const storage = useContext(StorageContext);
  const isDeleting = useStateful<boolean>(false);
  const isRegByOther = useStateful(false);
  const navigateToOversiktJaktdager = () => {
    navigate("/oversikt", { state: { activeTab: 0 } });
  };
  const [sumSettOgSkutt, setSumSettOgSkutt] = useState<SumSettOgSkuttJaktdag>(
    sumSettOgSkuttJaktdagDefault(),
  );
  const [storedJaktdag, setStoredJaktdag] = useState<StorageItem<Jaktdag> | null | undefined>(null);
  const [jaktdag, setJaktdag] = useState<Jaktdag | null | undefined>(
    queryClient.getQueryData(["jaktdagId-" + jaktdagId, { jaktdagId: jaktdagId }]),
  );

  const { refetchJaktdataForJaktomrade } = useJaktdataHelper();
  const clientState = useClientState();
  const isOnline = clientState === "Online";

  useEffect(() => {
    if (jaktdag?.Art && jaktdag.Dato) {
      setHeader(
        `${t(
          "pages.oversikt.jaktdagDetaljer.jaktdagFor",
        )} ${jaktdag?.Art?.toLowerCase()} ${formatDmy(jaktdag?.Dato, true)}`,
      );
    } else {
      setHeader(`${t("pages.oversikt.jaktdagDetaljer.jaktdagFor")} ... ...`);
    }
  }, [jaktdag?.Art, jaktdag?.Dato]);

  const jaktdagQuery = useQuery({
    meta: { url: `/jaktdager/${jaktdagId}` },
    queryKey: ["jaktdagId-" + jaktdagId, { jaktdagId: jaktdagId }],
    queryFn: getJaktdagQuery,
    retry: 1,
    enabled: storage.get<Jaktdag>({ id: jaktdagId }, "Jaktdag") === undefined,
  });

  useEffect(() => {
    if (jaktdag) {
      omrade.set(getOmradeForJaktdag(jaktdag, appData));
      canEdit.set(!jaktdag.Godkjent);
      setSumSettOgSkutt(sumSettOgSkuttJaktdag(jaktdag));
      isRegByOther.set(appData.jegeropplysninger.data?.Jegernummer !== jaktdag?.Jegernummer);
    }
  }, [jaktdag]);

  useEffect(() => {
    const stored = storage.get<Jaktdag>({ id: jaktdagId }, "Jaktdag");

    if (stored) {
      setStoredJaktdag(stored);
      if (stored.operation !== "delete" || !!stored.errors) {
        setJaktdag(storage.setIndividerForJaktdag(stored.data));
      }
    }

    if (
      jaktdagQuery.isSuccess &&
      typeof jaktdagQuery.data !== "string" &&
      (stored?.syncStatus === undefined || stored.syncStatus === "unconfirmed")
    ) {
      const apiJaktdag = jaktdagQuery.data as Jaktdag;

      if (stored && stored.data) {
        stored.data.Individer = apiJaktdag.Individer;
        setJaktdag(storage.setIndividerForJaktdag(stored.data));
      } else {
        setJaktdag(storage.setIndividerForJaktdag(apiJaktdag));
      }
      setStoredJaktdag(undefined);
    }
  }, [jaktdagQuery.isSuccess, jaktdagQuery.data, isOnline]);

  const handleDeleteClick = () => {
    const isRegByOtherText = (
      <Text>
        {t(tBase + "deleteDialogMessageRegByOther1")}
        <strong className="font-bold">{jaktdag?.RegistrertAv}</strong>
        {t(tBase + "deleteDialogMessageRegByOther2")}
      </Text>
    );

    deleteDialog.show({
      message: isRegByOther.value ? isRegByOtherText : t(tBase + "deleteDialogMessage"),
    });
  };

  const handleConfirmDelete = () => {
    if (!jaktdag?.Id) return;
    isDeleting.set(true);

    jaktdataApi
      .deleteJaktdag(jaktdag?.Id)
      .then(() => {
        const jaktomradeId = omrade.value?.Id || jaktdag.Jaktfelt || "";
        const sesong = hentJaktaar();
        const jaktdagerQueryKey = {
          queryKey: [
            "jaktdager",
            {
              jaktomradeId,
              sesong,
            },
          ],
        };
        storage.delete({ id: jaktdag.Id }, "Jaktdag");

        const existingJaktdager = queryClient.getQueryData<{ data: { Id?: string } }[]>(
          jaktdagerQueryKey.queryKey,
        );
        if (existingJaktdager) {
          queryClient.setQueryData(
            jaktdagerQueryKey.queryKey,
            existingJaktdager.filter((j) => j.data?.Id !== jaktdag.Id),
          );
        }

        queryClient.invalidateQueries(jaktdagerQueryKey).then(() => {
          queryClient.refetchQueries(jaktdagerQueryKey).then(() => {
            uiContext.showSnackbar({
              message: t(tBase + "jaktdagDeletedMessage"),
              severity: AlertVariant.Success,
            });
            isDeleting.set(false);
            refetchJaktdataForJaktomrade(jaktdag.Jaktfelt).then(() => {
              navigateToOversiktJaktdager();
            });
          });
        });
      })
      .catch((e: ApiError) => {
        if (e.axiosError?.code === "ERR_NETWORK" || e.axiosError?.code === "ECONNABORTED") {
          // remove any pending create individer, not deletes or edits, as those can be neccessary to keep
          const storedIndivider = storage
            .getAll<Individ>("Individ")
            .filter(
              (individ) => individ.data.JaktdagId === jaktdag.Id && individ.operation === "create",
            );
          storedIndivider.forEach((individ) => storage.delete({ id: individ.data.Id }, "Individ"));

          const storedJaktdag = storage.get<JaktdagResponse>({ id: jaktdag.Id }, "Jaktdag");

          const jaktomradeIdForCache = omrade.value?.Id || jaktdag.Jaktfelt || "";
          const sesong = hentJaktaar();
          const jaktdagerCacheKey = ["jaktdager", { jaktomradeId: jaktomradeIdForCache, sesong }];
          const skuttedyrCacheKey = ["skuttedyr", { jaktomradeId: jaktomradeIdForCache, sesong }];

          const removeFromCache = () => {
            const existingJaktdager = queryClient.getQueryData<{ data: { Id?: string } }[]>(jaktdagerCacheKey);
            if (existingJaktdager) {
              queryClient.setQueryData(jaktdagerCacheKey, existingJaktdager.filter((j) => j.data?.Id !== jaktdag.Id));
            }
            const existingIndivider = queryClient.getQueryData<{ data: { JaktdagId?: string } }[]>(skuttedyrCacheKey);
            if (existingIndivider) {
              queryClient.setQueryData(skuttedyrCacheKey, existingIndivider.filter((i) => i.data?.JaktdagId !== jaktdag.Id));
            }
          };

          if (
            storedJaktdag?.operation === "create" &&
            storedJaktdag?.syncStatus !== "unconfirmed"
          ) {
            storage.delete({ id: jaktdag.Id }, "Jaktdag");
            removeFromCache();
            uiContext.showSnackbar({
              message: t("pages.oversikt.jaktdagDetaljer.jaktdagDeletedMessage"),
              severity: AlertVariant.Success,
            });
            navigateToOversiktJaktdager();
            return;
          }

          storage.add({
            objectType: "Jaktdag",
            objectKey: { id: jaktdag.Id },
            data: jaktdag,
            isPending: true,
            operation: "delete",
            lastUpdated: new Date(),
          });

          removeFromCache();

          uiContext.showSnackbar({
            message: t("sync.noNetworkWillSyncLater"),
            severity: AlertVariant.Info,
          });

          navigateToOversiktJaktdager();
          return;
        }
        if (e.message.includes("Jaktdag med Id må finnes")) {
          //trying to delete something that has not been created yet. Delete the local jaktdag, and belonging individer instead
          storage.delete({ id: jaktdag.Id }, "Jaktdag");
          storage
            .getIndividerForJaktdag(jaktdag.Id)
            .forEach((i) => storage.delete({ id: i.data.Id }, "Individ"));

          uiContext.showSnackbar({
            message: t(tBase + "jaktdagDeletedMessage"),
            severity: AlertVariant.Success,
          });
          refetchJaktdataForJaktomrade(jaktdag.Jaktfelt).then(() => {
            navigateToOversiktJaktdager();
          });
          return;
        }

        uiContext.showSnackbar({ message: e.message, severity: AlertVariant.Warning });
        refetchJaktdataForJaktomrade(jaktdag.Jaktfelt).then(() => {
          navigateToOversiktJaktdager();
        });
      });
  };

  const deleteDialog = useConfirmationDialog({
    title: t(tBase + "deleteDialogTitle"),
    message: "",
    onConfirm: handleConfirmDelete,
    confirmButtonText: t("actions.delete"),
  });

  const tCounterLabels = "pages.registrerArt." + jaktdag?.Art?.toLowerCase() + ".sett";

  if (jaktdagQuery.isSuccess && !jaktdag) {
    return <TomtResultat>{t("errors.notFound", { type: "Jaktdag" })}</TomtResultat>;
  }

  const removeStoredJaktdag = () => {
    if (!storedJaktdag) {
      return;
    }

    storage.delete(storedJaktdag.objectKey, "Jaktdag");

    if (storedJaktdag.operation === "create") {
      //If the stored jaktdag is a create, remove individer as well, so that there will be no individer without a connected jaktdag
      storage
        .getIndividerForJaktdag(storedJaktdag.data.Id)
        .forEach((i) => storage.delete({ id: i.data.Id }, "Individ"));
    }
    refetchJaktdataForJaktomrade(storedJaktdag.data.Jaktfelt).then(() => {
      navigateToOversiktJaktdager();
    });
  };

  if ((isDeleting.value || jaktdagQuery.isFetching) && !jaktdag?.Id) {
    return (
      <TomtResultat>
        <CircularProgress thickness={7} size={80} />
      </TomtResultat>
    );
  }

  return !jaktdag ? (
    <div className="m-4">{t("errors.notFound", { type: "Jaktdag" })}</div>
  ) : (
    <div className="pt-4 h-full">
      <div className="h-full flex justify-between flex-col">
        <div className="m-4 mt-0">
          <Header headerLvl={2} variant={TextVariant.xlarge} serif className="mb-4 mt-2">
            {omrade.value?.Navn} {omrade.value?.Id}
          </Header>
          <AlertWrapper>
            {!canEdit.value && jaktdag?.Godkjent && (
              <AlertDefault variant={AlertVariant.Info}>{t(tBase + "kanIkkeEndres")}</AlertDefault>
            )}
            {jaktdag.UtenforJakttid && (
              <AlertDefault variant={AlertVariant.Warning}>
                {t("pages.oversikt.jaktdagUtenforJakttid")}
              </AlertDefault>
            )}
            {sumSettOgSkutt.isInconsistent && (
              <AlertDefault variant={AlertVariant.Error}>
                {t("pages.oversikt.inkonsistensSettSkutt")}
              </AlertDefault>
            )}

            {storedJaktdag?.isPending && (
              <AlertDefault variant={AlertVariant.Sync}>{t("sync.pending")}</AlertDefault>
            )}

            {storedJaktdag?.syncStatus === "error" && (
              <AlertDefault
                variant={AlertVariant.Error}
                bottomElement={
                  isOnline && storedJaktdag?.operation != "create" ? (
                    <ButtonDefault
                      id="fjernEndringer"
                      onClick={removeStoredJaktdag}
                      label={t("actions.discardAndRestore")}
                    />
                  ) : (
                    <></>
                  )
                }
              >
                {t("sync.syncError")}
                {storedJaktdag.errors && <ErrorList errors={storedJaktdag.errors} />}
              </AlertDefault>
            )}
          </AlertWrapper>

          <BottomLine />
          <div className="flex text-lg my-5">
            <dl className="flex ml-4">
              <dt>{t("components.jaktdagCard.hunters")}:</dt>
              <dd className="font-bold ml-2">
                {(jaktdag.AntallJegere || 0) + (jaktdag.AntallJegereInnmark || 0)}
              </dd>
            </dl>
            <dl className="flex ml-4">
              <dt>{t(tBase + "timerJaktet")}:</dt>
              <dd className="font-bold ml-2">
                {(jaktdag.AntallTimerJaktet || 0) + (jaktdag.AntallTimerJaktetInnmark || 0)}
              </dd>
            </dl>
          </div>

          {/* Innmark - kun hjort */}
          {jaktdag.Innmark && (
            <>
              <AccordionDefault
                defaultOpen={false}
                count={sumSettOgSkutt.sumSettInnmark}
                error={sumSettOgSkutt.errors.innmark}
                disabled={sumSettOgSkutt.sumSettInnmark === 0}
                title={
                  t("pages.registrerArt.sett") +
                  " - " +
                  t("pages.fellingsinfo.innmark").toLowerCase()
                }
                useErrorIcon
              >
                <CounterWithLabel
                  label={t(`${tCounterLabels}.SettBukkInnmark`)}
                  count={jaktdag.SettBukkInnmark}
                />
                <CounterWithLabel
                  label={t(`${tCounterLabels}.SettSpissbukkInnmark`)}
                  count={jaktdag.SettSpissbukkInnmark}
                />
                <CounterWithLabel
                  label={t(`${tCounterLabels}.SettKolleInnmark`)}
                  count={jaktdag.SettKolleInnmark}
                />
                <CounterWithLabel
                  label={t(`${tCounterLabels}.SettKalvInnmark`)}
                  count={jaktdag.SettKalvInnmark}
                />
                <CounterWithLabel
                  label={t(`${tCounterLabels}.SettUkjenteInnmark`)}
                  count={jaktdag.SettUkjenteInnmark}
                />
              </AccordionDefault>

              <AccordionDefault
                defaultOpen={false}
                title={
                  t("pages.registrerArt.skutt") +
                  " - " +
                  t("pages.fellingsinfo.innmark").toLowerCase()
                }
                count={
                  jaktdag.Individer?.filter(
                    (skuttDyr) => skuttDyr.Terreng === Terreng.Innmark.toString(),
                  ).length || 0
                }
                disabled={
                  jaktdag.Individer?.filter(
                    (skuttDyr) => skuttDyr.Terreng === Terreng.Innmark.toString(),
                  ).length < 1
                }
                useErrorIcon
              >
                <div className="gap-4 flex flex-col">
                  {jaktdag.Individer?.filter(
                    (skuttDyr) => skuttDyr.Terreng === Terreng.Innmark.toString(),
                  ).map((skuttDyr) => (
                    <SkuttDyrCard
                      key={skuttDyr.Id}
                      skuttDyr={skuttDyr}
                      syncStatus={storage.get<Individ>({ id: skuttDyr.Id }, "Individ")?.syncStatus}
                      queryClient={queryClient}
                      clientState={clientState}
                      storage={storage}
                    />
                  ))}
                </div>
              </AccordionDefault>
            </>
          )}

          {/* Utmark */}
          {((jaktdag.Art === ArtType.Hjort && jaktdag.Utmark) || jaktdag.Art !== ArtType.Hjort) && (
            <>
              <AccordionDefault
                defaultOpen={false}
                count={sumSettOgSkutt.sumSett}
                error={sumSettOgSkutt.errors.utmark}
                disabled={sumSettOgSkutt.sumSett === 0}
                title={`${t("pages.registrerArt.sett")} ${
                  jaktdag.Utmark ? " - " + t("pages.fellingsinfo.utmark").toLowerCase() : ""
                }`}
                useErrorIcon
              >
                <CounterWithLabel
                  label={t(
                    `${tCounterLabels}.${
                      jaktdag.Art === ArtType.Elg ? "SettOkseEllerBukk" : "SettBukk"
                    }`,
                  )}
                  count={jaktdag.SettOkseEllerBukk}
                />
                <CounterWithLabel
                  label={t(`${tCounterLabels}.SettSpissbukk`)}
                  count={jaktdag.SettSpissbukk}
                />
                <CounterWithLabel
                  label={t(
                    `${tCounterLabels}.${
                      jaktdag.Art === ArtType.Elg ? "SettKyrUtenKalvEllerKolle" : "SettKolle"
                    }`,
                  )}
                  count={jaktdag.SettKyrUtenKalvEllerKolle}
                />
                <CounterWithLabel
                  label={t(`${tCounterLabels}.SettKyrMed1Kalv`)}
                  count={jaktdag.SettKyrMed1Kalv}
                />
                <CounterWithLabel
                  label={t(`${tCounterLabels}.SettKyrMed2EllerFlereKalver`)}
                  count={jaktdag.SettKyrMed2EllerFlereKalver}
                />
                <CounterWithLabel
                  label={t(
                    `${tCounterLabels}.${
                      jaktdag.Art === ArtType.Elg ? "SettKalverAlene" : "SettKalv"
                    }`,
                  )}
                  count={jaktdag.SettKalverAlene}
                />
                {jaktdag.Art === ArtType.Elg && (
                  <CounterWithLabel
                    label={t(`${tCounterLabels}.sumKalver`)}
                    count={sumSettOgSkutt.settKalver}
                  />
                )}
                <CounterWithLabel
                  label={t(`${tCounterLabels}.SettUkjente`)}
                  count={jaktdag.SettUkjente}
                />
              </AccordionDefault>

              <AccordionDefault
                defaultOpen={false}
                title={`${t("pages.registrerArt.skutt")} ${
                  jaktdag.Utmark ? " - " + t("pages.fellingsinfo.utmark").toLowerCase() : ""
                }`}
                count={
                  jaktdag.Individer?.filter(
                    (skuttDyr) => skuttDyr.Terreng !== Terreng.Innmark.toString(),
                  ).length || 0
                }
                disabled={
                  jaktdag.Individer?.filter(
                    (skuttDyr) => skuttDyr.Terreng !== Terreng.Innmark.toString(),
                  ).length < 1
                }
                error={sumSettOgSkutt.errors.utmark}
                useErrorIcon
              >
                <div className="gap-4 flex flex-col">
                  {jaktdag.Individer?.filter(
                    (skuttDyr) => skuttDyr.Terreng !== Terreng.Innmark.toString(),
                  ).map((skuttDyr) => (
                    <SkuttDyrCard
                      key={skuttDyr.Id}
                      skuttDyr={skuttDyr}
                      syncStatus={storage.get<Individ>({ id: skuttDyr.Id }, "Individ")?.syncStatus}
                      queryClient={queryClient}
                      clientState={clientState}
                      storage={storage}
                    />
                  ))}
                </div>
              </AccordionDefault>
            </>
          )}
        </div>
        {canEdit.value && (
          <BottomBarDouble
            buttonOne={
              <ButtonDefault
                id="JaktdagDetaljerSlett"
                label={t("actions.delete")}
                variant={ButtonVariant.Tertiary}
                onClick={handleDeleteClick}
                icon={<DeleteIcon />}
              />
            }
            buttonTwo={
              <ButtonDefault
                id="JaktdagDetaljerEndre"
                label={t("actions.endre")}
                variant={ButtonVariant.Primary}
                onClick={() => navigate(`/registrering/jaktdag/${jaktdag.Id}`)}
                icon={<EditIcon />}
              />
            }
          />
        )}
      </div>
      {deleteDialog.element}
    </div>
  );
}
